import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { installErrorReporting } from './lib/errors';
import './styles/tokens.css';
import './styles/app.css';

// Silence the benign ResizeObserver warning + report real uncaught errors
// (pp_js_error) so the Clarity "JS error" metric reflects actual problems.
installErrorReporting();

// The SPA mounts in two contexts:
//  * the app-proxy shell at /apps/print (worker shell.ts / dev index.html) —
//    canonical home of the status page + emailed deep links;
//  * a theme app block on any theme page (e.g. /pages/order-prints) — the block
//    (shopify/extensions/print-portal-block) renders the same root div with
//    data-rpl-embed and loads the scope-prefixed embed.css.
// The router basename follows the mount: proxy paths keep '/apps/print', a
// theme page uses its own path so useSearchParams / setParams stay correct.
const PROXY_BASENAME = '/apps/print';

function resolveBasename(): string {
  const path = window.location.pathname;
  if (path.startsWith(PROXY_BASENAME)) return PROXY_BASENAME;
  return path.replace(/\/+$/, '') || '/';
}

let root: Root | null = null;

/** Embed height model: publish the block's offset from the document top so
 *  embed.css can size the app to exactly fill the rest of the viewport
 *  (min-height: 100svh - top - manual offset) — then the sticky footbar hugs
 *  the viewport bottom regardless of the theme header's height. */
function measureEmbedTop(rootEl: HTMLElement): void {
  const top = Math.max(0, Math.round(rootEl.getBoundingClientRect().top + window.scrollY));
  rootEl.style.setProperty('--rpl-embed-top', `${top}px`);
}

function mount(): void {
  const rootEl =
    document.getElementById('rpl-print-portal-root') ?? document.getElementById('root');
  if (!rootEl) throw new Error('#rpl-print-portal-root element not found');
  if (rootEl.hasAttribute('data-rpl-embed')) {
    rootEl.classList.add('rpl-embed');
    const measure = () => measureEmbedTop(rootEl);
    measure();
    window.addEventListener('resize', measure);
    // Late layout shifts above the block (web fonts, announcement bars) move
    // our document-top offset after first paint — re-measure when they settle.
    window.addEventListener('load', measure);
    document.fonts?.ready.then(measure).catch(() => {});
  }

  root?.unmount();
  root = createRoot(rootEl);
  root.render(
    <StrictMode>
      <BrowserRouter basename={resolveBasename()}>
        <App />
      </BrowserRouter>
    </StrictMode>,
  );
}

mount();

// Theme editor re-renders the section's DOM when the merchant tweaks block
// settings — re-mount into the fresh root div (no-op on the storefront).
document.addEventListener('shopify:section:load', () => {
  if (document.getElementById('rpl-print-portal-root')) mount();
});
